import { useRef, type PointerEvent as ReactPointerEvent } from "react";

interface UseSwipeNavigationOptions {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  enabled?: boolean;
  threshold?: number;
}

/**
 * Horizontal-swipe detection on touch pointers.
 * Vertical-dominant drags are ignored so page scroll keeps working.
 */
export function useSwipeNavigation({
  onSwipeLeft,
  onSwipeRight,
  enabled = true,
  threshold = 50,
}: UseSwipeNavigationOptions) {
  const startRef = useRef<{ x: number; y: number } | null>(null);

  if (!enabled) {
    return {};
  }

  const onPointerDown = (e: ReactPointerEvent) => {
    if (e.pointerType !== "touch") return;
    startRef.current = { x: e.clientX, y: e.clientY };
  };

  const onPointerUp = (e: ReactPointerEvent) => {
    const start = startRef.current;
    startRef.current = null;
    if (!start || e.pointerType !== "touch") return;

    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;

    if (Math.abs(dx) < threshold) return;
    if (Math.abs(dy) > Math.abs(dx)) return;

    if (dx < 0) onSwipeLeft?.();
    else onSwipeRight?.();
  };

  return {
    onPointerDown,
    onPointerUp,
    onPointerCancel: () => {
      startRef.current = null;
    },
  };
}
